import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import { authAPI, adminAPI } from '../lib/api';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetError, setResetError] = useState('');
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoLoading, setLogoLoading] = useState(true);
  const [logoError, setLogoError] = useState(false);

  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);

  // Load company logo
  useEffect(() => {
    const loadLogo = async () => {
      try {
        const response = await adminAPI.getCompanyLogo();
        if (response.data?.logo_url) {
          setLogoUrl(response.data.logo_url);
          setLogoError(false);
        } else {
          setLogoLoading(false);
        }
      } catch (error) {
        console.error('Failed to load logo:', error);
        setLogoLoading(false);
      }
    };
    loadLogo();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      navigate('/');
    } else {
      setError(result.error || 'Login failed');
    }

    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetError('');
    setResetSuccess(false);
    setResetLoading(true);

    try {
      await authAPI.resetPassword(resetEmail);
      setResetSuccess(true);
      setTimeout(() => {
        setShowForgotPassword(false);
        setResetEmail('');
        setResetSuccess(false);
      }, 3000);
    } catch (err) {
      setResetError(err.response?.data?.message || 'Failed to send reset email');
    } finally {
      setResetLoading(false);
    }
  };

  const closeForgotPasswordModal = () => {
    setShowForgotPassword(false);
    setResetEmail('');
    setResetError('');
    setResetSuccess(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="max-w-md w-full">
        <div className="card">
          <div className="text-center mb-8">
            {/* Company Logo */}
            <div className="flex justify-center mb-6">
              {logoUrl && !logoError ? (
                <img
                  src={logoUrl}
                  alt="Company Logo"
                  className="h-20 w-auto"
                  onLoad={() => setLogoLoading(false)}
                  onError={() => {
                    setLogoError(true);
                    setLogoLoading(false);
                  }}
                />
              ) : !logoLoading ? (
                <div className="h-20 w-20 bg-primary-600 rounded-full flex items-center justify-center text-white text-3xl font-bold">
                  Alzu
                </div>
              ) : null}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                placeholder="you@example.com"
                required
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input"
                placeholder="••••••••"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="text-sm text-primary-600 hover:text-primary-700"
            >
              Forgot password?
            </button>
          </div>

          <div className="mt-6 text-center">
            <div className="flex justify-center mb-2">
              <img
                src="/images/doxolve_logo.png"
                alt="Doxolve Logo"
                className="h-12 w-auto"
              />
            </div>
            <p className="text-gray-600 text-xs">
              AI Document Solution by{' '}
              <a
                href="https://uit.co.za"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-primary-600 hover:text-primary-700 transition-colors duration-200"
              >
                Universal Information Technologies
              </a>
            </p>
          </div>
        </div>

        {/* Forgot Password Modal */}
        {showForgotPassword && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Reset Password
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Enter your email address and we'll send you a link to reset your password.
              </p>

              {resetSuccess && (
                <div className="mb-4 p-3 rounded-lg bg-green-50 text-green-700 border border-green-200">
                  <p className="text-sm font-medium">✓ Password reset email sent!</p>
                  <p className="text-xs mt-1">Check your inbox for the reset link.</p>
                </div>
              )}

              {resetError && (
                <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 border border-red-200">
                  <p className="text-sm">{resetError}</p>
                </div>
              )}

              <form onSubmit={handleForgotPassword}>
                <div className="mb-4">
                  <label htmlFor="resetEmail" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    id="resetEmail"
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="input"
                    required
                    disabled={resetLoading || resetSuccess}
                  />
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={closeForgotPasswordModal}
                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    disabled={resetLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={resetLoading || resetSuccess}
                    className="btn-primary"
                  >
                    {resetLoading ? 'Sending...' : 'Send Reset Link'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;
